import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AppContext from '../../../Context/AppContext';
import configData from '../../../config';
import iconUser from '../../../Assets/img/userIcon.jpg';
import AchievementsPanel from './AchievementsPanel';
import OolaScorePanel from './OolaScorePanel';
import ProgramsProgressPanel from './ProgramsProgressPanel';
import './profileTokens.css';
import './ProfileWorkspace.css';

const MAX_BYTES = 2 * 1024 * 1024;

/**
 * Dashboard V2 Profile workspace — photo, Oola Score, and achievements
 * (same APIs as V1). Does not touch classic Dashboard / UserInfo.
 */
const ProfileWorkspace = ({
    profile,
    onProfileRefresh,
    onBack,
    progress,
    active = true,
}) => {
    const { checkUserSession } = useContext(AppContext);
    const { token, userId } = checkUserSession();
    const fileRef = useRef(null);

    const [imageHash, setImageHash] = useState(Date.now());
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadOk, setUploadOk] = useState('');
    const [latestScore, setLatestScore] = useState(null);
    const [earnedCount, setEarnedCount] = useState(null);

    const firstName = profile?.firstName || '';
    const lastName = profile?.lastName || '';
    const fullName =
        [firstName, lastName].filter(Boolean).join(' ') || 'Your profile';

    const apiBase = String(configData.SERVER_URL || '').replace(/\/?$/, '/');
    const isLocalApi = /localhost|127\.0\.0\.1/i.test(apiBase);
    const avatarSrc = profile?.pictureFileName
        ? isLocalApi
            ? `/media/profile/${profile.pictureFileName}?${imageHash}`
            : `${configData.SERVER_URL_IMG}/media/profile/${profile.pictureFileName}?${imageHash}`
        : iconUser;

    useEffect(() => {
        if (!uploadOk) return undefined;
        const timer = window.setTimeout(() => setUploadOk(''), 3200);
        return () => window.clearTimeout(timer);
    }, [uploadOk]);

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !token || !userId) return;

        setUploadError('');
        setUploadOk('');

        if (!file.type || !file.type.includes('image')) {
            setUploadError('Please choose an image file.');
            return;
        }
        if (file.size > MAX_BYTES) {
            setUploadError('Image must be 2MB or smaller.');
            return;
        }

        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('image', file);

        const uploadUrl =
            configData.SERVER_URL_IMGUpload ||
            `${apiBase}Customer/UploadProfilePicture`;

        setUploading(true);
        try {
            const response = await axios.post(uploadUrl, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 201) {
                setImageHash(Date.now());
                setUploadOk('Photo updated.');
                if (typeof onProfileRefresh === 'function') {
                    await onProfileRefresh();
                }
            } else {
                setUploadError('Could not upload photo. Try again.');
            }
        } catch (err) {
            const apiMsg =
                typeof err?.response?.data === 'string'
                    ? err.response.data
                    : err?.response?.data?.title ||
                      err?.response?.statusText ||
                      '';
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                setUploadError(
                    'Upload unauthorized. Check that the API URL matches your login environment.'
                );
            } else if (apiMsg) {
                setUploadError(apiMsg);
            } else {
                setUploadError('Could not upload photo. Try again.');
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <section
            className="dv2-pw dv2-glass dv2-pw--card"
            hidden={!active}
            aria-hidden={!active}
        >
            <div className="dv2-pw__top">
                <button type="button" className="dv2-pw__back" onClick={onBack}>
                    ← Back to dashboard
                </button>
            </div>

            <header className="dv2-pw__identity">
                <div className="dv2-pw__identity-main">
                    <div className="dv2-pw__avatar-wrap">
                        <img
                            className="dv2-pw__avatar"
                            src={avatarSrc}
                            alt=""
                            onError={(e) => {
                                e.currentTarget.src = iconUser;
                            }}
                        />
                        <button
                            type="button"
                            className="dv2-pw__avatar-edit"
                            disabled={uploading}
                            aria-label="Change profile photo"
                            title="Change profile photo"
                            onClick={() => fileRef.current?.click()}
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                aria-hidden
                            >
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="dv2-pw__file"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                    </div>

                    <div className="dv2-pw__identity-copy">
                        <p className="dv2-pw__eyebrow">Profile</p>
                        <h1 className="dv2-pw__name">{fullName}</h1>
                        {profile?.customerLevel ? (
                            <span className="dv2-pw__level">
                                {String(profile.customerLevel).toUpperCase()}
                            </span>
                        ) : null}
                        <p className="dv2-pw__hint">
                            {uploading
                                ? 'Uploading photo…'
                                : 'Tap the camera to update your photo.'}
                        </p>
                        {uploadError ? (
                            <p
                                className="dv2-pw__msg dv2-pw__msg--error"
                                role="alert"
                            >
                                {uploadError}
                            </p>
                        ) : null}
                        {uploadOk ? (
                            <p className="dv2-pw__msg dv2-pw__msg--ok">
                                {uploadOk}
                            </p>
                        ) : null}
                    </div>
                </div>

                <aside className="dv2-pw__journey" aria-label="Your Oola journey">
                    <p className="dv2-pw__journey-kicker">Your Oola journey</p>
                    <div className="dv2-pw__journey-row">
                        <div className="dv2-pw__journey-stat">
                            <strong>
                                {latestScore != null ? latestScore : '—'}
                            </strong>
                            <span>Oola Score</span>
                        </div>
                        <div className="dv2-pw__journey-stat">
                            <strong>
                                {earnedCount != null ? earnedCount : '—'}
                            </strong>
                            <span>Achievements</span>
                        </div>
                        <div className="dv2-pw__journey-stat">
                            <strong>
                                {profile?.customerLevel
                                    ? String(profile.customerLevel)
                                    : '—'}
                            </strong>
                            <span>Current Level</span>
                        </div>
                    </div>
                </aside>
            </header>

            <div className="dv2-pw__overview">
                <div className="dv2-pw__oscore">
                    <OolaScorePanel
                        active={active}
                        onLatestScore={setLatestScore}
                    />
                </div>
                <div className="dv2-pw__achievements">
                    <AchievementsPanel
                        active={active}
                        goalsCompleted={progress?.goalsCompleted ?? 0}
                        progress={progress}
                        onEarnedCount={setEarnedCount}
                    />
                </div>
            </div>

            <ProgramsProgressPanel
                active={active}
                progress={progress}
                profile={profile}
            />
        </section>
    );
};

export default ProfileWorkspace;
